import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { ChefHat, FileSpreadsheet, FileText, Info, Package, Play, Plus, Trash2, Layers, ChevronDown } from 'lucide-react';

function prettyQty(qty, unit) {
  const u = (unit || 'kg').toLowerCase();
  const n = Number(qty) || 0;
  if (u === 'kg' || u === 'l') {
    if (n >= 1) return `${n.toFixed(3)} ${u}`;
    return `${Math.round(n * 1000)} ${u === 'kg' ? 'g' : 'ml'}`;
  }
  if (u === 'g') return n >= 1000 ? `${(n / 1000).toFixed(3)} kg` : `${Math.round(n)} g`;
  if (u === 'ml') return n >= 1000 ? `${(n / 1000).toFixed(3)} L` : `${Math.round(n)} ml`;
  if (u === 'pc') return `${Math.round(n)} pc`;
  return `${n.toFixed(3)} ${u}`;
}

let _rowSeq = 0;
const newRow = (recipeId = '', qty = '') => ({ key: `r${Date.now()}${++_rowSeq}`, recipe_id: recipeId, planned_qty: qty });

export default function ProductionPlannerPage({
  title = 'Production Planner',
  subtitle = 'Plan one or many dishes together — the picklist is aggregated across all recipes.',
  testIdPrefix = 'production',
}) {
  const { API, getHeaders, user, activeOutlet } = useAuth();
  const isAdmin = ['admin', 'area_manager', 'super_admin'].includes(user?.role);
  const isStudio = user?.role === 'studio_chef';
  const [searchParams, setSearchParams] = useSearchParams();

  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState('');
  const [showAll, setShowAll] = useState(false);

  const [recipes, setRecipes] = useState([]);
  const [excludedCount, setExcludedCount] = useState(0);
  const [rows, setRows] = useState([newRow()]);

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [exporting, setExporting] = useState('');

  // Load outlet list (admin only — studio chefs operate in a single workspace, no outlets)
  useEffect(() => {
    if (isStudio) {
      // Studio mode: bypass outlet flow entirely. Recipes are scoped by tenant_id.
      return;
    }
    if (!isAdmin) {
      setOutletId(activeOutlet?.id || '');
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/outlets`, { headers: getHeaders() });
        const list = r.data || [];
        setOutlets(list);
        if (list.length && !outletId) setOutletId(list[0].id);
      } catch {
        toast.error('Failed to load outlets');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, isAdmin, isStudio]);

  const loadRecipes = useCallback(async () => {
    // Studio: no outlet gating. Enterprise: must have an outlet selected.
    if (!isStudio && !outletId) return;
    setLoading(true);
    try {
      const params = {};
      if (isStudio) {
        // Backend already forces include_all=true and skips outlet filtering for
        // studio_chef users — but we explicitly omit outlet_id for clarity.
      } else {
        params.outlet_id = outletId;
        if (isAdmin && showAll) params.include_all = true;
      }
      const r = await axios.get(`${API}/production/recipes`, { headers: getHeaders(), params });
      const list = r.data.items || [];
      setRecipes(list);
      setExcludedCount(r.data.excluded_count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load recipes');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, getHeaders, outletId, showAll, isAdmin, isStudio]);

  useEffect(() => { loadRecipes(); }, [loadRecipes]);

  // Pre-fill from URL params (e.g. /studio/production?recipe=<id>&qty=4) so the
  // "Plan this recipe" shortcut on /studio/recipes lands users one click away.
  useEffect(() => {
    const recipeParam = searchParams.get('recipe');
    const qtyParam = searchParams.get('qty');
    if (!recipeParam) return;
    if (!recipes.length) return;
    const match = recipes.find((r) => r.id === recipeParam && r.selectable);
    if (!match) return;
    setRows([{ key: `prefill-${recipeParam}`, recipe_id: recipeParam, planned_qty: String(qtyParam || '4') }]);
    // Clear params so a refresh doesn't re-trigger.
    const next = new URLSearchParams(searchParams);
    next.delete('recipe'); next.delete('qty');
    setSearchParams(next, { replace: true });
  }, [recipes, searchParams, setSearchParams]);

  const recipesById = useMemo(() => Object.fromEntries(recipes.map((r) => [r.id, r])), [recipes]);
  const selectedIds = useMemo(() => new Set(rows.map((r) => r.recipe_id).filter(Boolean)), [rows]);

  const updateRow = (key, patch) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (key) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));
  const addRow = () => setRows((prev) => [...prev, newRow()]);

  const validRows = rows.filter((r) => r.recipe_id && Number(r.planned_qty) > 0);
  const canGenerate = validRows.length > 0 && !generating && !loading;

  const buildPayloadItems = () => validRows.map((r) => ({ recipe_id: r.recipe_id, planned_qty: Number(r.planned_qty) }));

  const generate = async () => {
    if (validRows.length === 0) { toast.error('Add at least one recipe with quantity'); return; }
    // Block if any selected recipe is not selectable
    const blocked = validRows.find((r) => {
      const rec = recipesById[r.recipe_id];
      return rec && !rec.selectable;
    });
    if (blocked) {
      const rec = recipesById[blocked.recipe_id];
      toast.error(`"${rec.name}" can't be used: ${rec.excluded_reason}`);
      return;
    }
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/production/plan/multi`,
        { items: buildPayloadItems() },
        { headers: getHeaders() },
      );
      setPlan(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate picklist');
    } finally {
      setGenerating(false);
    }
  };

  const exportFile = async (format) => {
    if (validRows.length === 0) return;
    setExporting(format);
    try {
      const res = await axios.post(
        `${API}/production/plan/export`,
        { items: buildPayloadItems(), format },
        { headers: getHeaders(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], {
        type: format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `picklist_aggregated_${validRows.length}recipes.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting('');
    }
  };

  const selectableList = recipes.filter((r) => r.selectable);
  const notSelectableList = recipes.filter((r) => !r.selectable);

  return (
    <div data-testid={`${testIdPrefix}-page`} className="w-full pb-28">
      <div className="mb-5">
        <h2 className="text-xl sm:text-2xl tracking-tight font-semibold text-[#1A2421]">{title}</h2>
        <p className="text-sm text-[#5C6B64] mt-1">{subtitle}</p>
      </div>

      {/* Controls: Outlet + Show-all (admin) */}
      {isAdmin && (
        <Card className="border-[#E2E8DF] shadow-sm mb-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold">Outlet</label>
                <Select value={outletId} onValueChange={setOutletId}>
                  <SelectTrigger className="h-9 mt-1" data-testid={`${testIdPrefix}-outlet-select`}>
                    <SelectValue placeholder="Select an outlet" />
                  </SelectTrigger>
                  <SelectContent>
                    {outlets.map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 h-9 px-3 rounded-md bg-[#F9F9F8] border border-[#E2E8DF]">
                <Switch
                  checked={showAll}
                  onCheckedChange={setShowAll}
                  data-testid={`${testIdPrefix}-show-all-toggle`}
                />
                <span className="text-xs text-[#1A2421] font-medium whitespace-nowrap">Show all recipes</span>
                <span className="text-[10px] text-[#5C6B64] whitespace-nowrap">
                  {showAll ? '(including unassigned)' : '(assigned only)'}
                </span>
              </div>
            </div>
            {showAll && excludedCount > 0 && (
              <div className="mt-3 text-[11px] text-[#5C6B64] flex items-center gap-1">
                <Layers className="w-3 h-3" />
                {excludedCount} recipe{excludedCount === 1 ? '' : 's'} not assigned to this outlet — will show greyed out with reason.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Multi-recipe rows */}
      <Card className="border-[#E2E8DF] shadow-sm mb-5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              <ChefHat className="w-4 h-4 text-[color:var(--color-primary)]" /> Recipes in plan
            </h3>
            <span className="text-[11px] text-[#5C6B64]">
              {validRows.length} of {rows.length} row{rows.length === 1 ? '' : 's'} ready
            </span>
          </div>

          {selectableList.length === 0 && !loading && (
            <div
              className="flex items-start gap-2 text-[12px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3"
              data-testid={`${testIdPrefix}-empty-state`}
            >
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">{isStudio ? 'No recipes yet.' : 'No recipes available for this outlet.'}</div>
                <div className="text-amber-800">
                  {isStudio
                    ? 'Create your first recipe in Recipe Studio — it will appear here automatically for production planning.'
                    : isAdmin
                    ? 'Assign final (non-sub) recipes to this outlet in the Recipes module, or enable "Show all recipes" above.'
                    : 'Ask your admin to assign recipes to your outlet from the Recipe Costing module.'}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {rows.map((row, idx) => {
              const rec = recipesById[row.recipe_id];
              const unit = rec?.qty_unit_label || 'portions';
              const notSelectable = rec && !rec.selectable;
              return (
                <div
                  key={row.key}
                  className={`grid grid-cols-[1fr_160px_auto] gap-2 items-center p-2 rounded-md border ${notSelectable ? 'border-rose-200 bg-rose-50/30' : 'border-[#E2E8DF] bg-white'}`}
                  data-testid={`${testIdPrefix}-row-${idx}`}
                >
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button" variant="outline"
                        className="h-9 w-full justify-between font-normal"
                        data-testid={`${testIdPrefix}-recipe-${idx}`}
                      >
                        {rec ? (
                          <span className="flex items-center gap-2 min-w-0">
                            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${(rec.category_type || 'food') === 'beverage' ? 'text-[#1D4ED8] bg-[#1D4ED8]/10 border-[#1D4ED8]/20' : 'text-emerald-700 bg-emerald-50 border-emerald-200'}`}>
                              {(rec.category_type || 'food') === 'beverage' ? 'Bev' : 'Food'}
                            </span>
                            <span className="truncate text-[#1A2421]">{rec.name}</span>
                          </span>
                        ) : (
                          <span className="text-[#5C6B64]">{loading ? 'Loading…' : 'Pick a recipe'}</span>
                        )}
                        <ChevronDown className="w-3.5 h-3.5 text-[#5C6B64] shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="p-0 w-[420px]">
                      <Command>
                        <CommandInput placeholder="Search recipe…" className="h-9" data-testid={`${testIdPrefix}-recipe-search-${idx}`} />
                        <CommandList>
                          <CommandEmpty>No recipes match.</CommandEmpty>
                          {selectableList.length > 0 && (
                            <CommandGroup heading="Recipes">
                              {selectableList.map((r) => {
                                const used = selectedIds.has(r.id) && r.id !== row.recipe_id;
                                return (
                                  <CommandItem
                                    key={r.id}
                                    value={`${r.name} ${r.category_type || 'food'}`}
                                    disabled={used}
                                    onSelect={() => updateRow(row.key, { recipe_id: r.id })}
                                    className="gap-2"
                                  >
                                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${(r.category_type || 'food') === 'beverage' ? 'text-[#1D4ED8] bg-[#1D4ED8]/10 border-[#1D4ED8]/20' : 'text-emerald-700 bg-emerald-50 border-emerald-200'}`}>
                                      {(r.category_type || 'food') === 'beverage' ? 'Bev' : 'Food'}
                                    </span>
                                    <span className="flex-1 text-[#1A2421]">{r.name}</span>
                                    <span className="text-[9px] uppercase tracking-wider text-[#5C6B64]">{r.qty_unit_label}</span>
                                    {used && (
                                      <span className="text-[9px] uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">in plan</span>
                                    )}
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          )}
                          {notSelectableList.length > 0 && (
                            <CommandGroup heading="Not selectable">
                              {notSelectableList.map((r) => (
                                <CommandItem key={r.id} value={r.name} disabled className="gap-2 opacity-60">
                                  <span className="flex-1 text-[#5C6B64]">{r.name}</span>
                                  <span className="text-[9px] uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">
                                    {r.excluded_reason}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  <div className="relative">
                    <Input
                      type="number" min="0" step="any"
                      placeholder="Qty"
                      value={row.planned_qty}
                      onChange={(e) => updateRow(row.key, { planned_qty: e.target.value })}
                      className={`h-9 text-right tabular-nums font-semibold ${rec ? 'pr-20' : 'pr-3'}`}
                      data-testid={`${testIdPrefix}-qty-${idx}`}
                    />
                    {rec && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-[#5C6B64] pointer-events-none">
                        {unit}
                      </span>
                    )}
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeRow(row.key)}
                    disabled={rows.length <= 1}
                    className="h-9 w-9 p-0 text-[#5C6B64] hover:text-rose-700"
                    data-testid={`${testIdPrefix}-remove-${idx}`}
                    aria-label="Remove recipe"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addRow}
            className="mt-3 h-8"
            data-testid={`${testIdPrefix}-add-row`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Recipe
          </Button>
        </CardContent>
      </Card>

      {/* Picklist */}
      <Card className="border-[#E2E8DF] shadow-sm">
        <CardContent className="p-0">
          <div className="flex items-center justify-between gap-3 p-4 border-b border-[#E2E8DF]">
            <div>
              <h3 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
                <Package className="w-4 h-4 text-[color:var(--color-primary)]" /> Aggregated Picklist
              </h3>
              {plan ? (
                <p className="text-xs text-[#5C6B64] mt-0.5">
                  {plan.recipe_count} recipe{plan.recipe_count === 1 ? '' : 's'} · {plan.items.length} raw ingredient{plan.items.length === 1 ? '' : 's'}
                </p>
              ) : (
                <p className="text-xs text-[#5C6B64] mt-0.5">Add recipes above and click Generate to see the combined raw-ingredient list.</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => exportFile('xlsx')}
                disabled={!plan || exporting !== ''}
                className="h-8"
                data-testid={`${testIdPrefix}-export-xlsx`}
              >
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> {exporting === 'xlsx' ? 'Exporting…' : 'Excel'}
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => exportFile('pdf')}
                disabled={!plan || exporting !== ''}
                className="h-8"
                data-testid={`${testIdPrefix}-export-pdf`}
              >
                <FileText className="w-3.5 h-3.5 mr-1" /> {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
              </Button>
            </div>
          </div>

          {/* Recipes-in-plan preview */}
          {plan && plan.recipes && plan.recipes.length > 0 && (
            <div className="px-4 py-3 border-b border-[#E2E8DF] flex flex-wrap gap-2">
              {plan.recipes.map((rec) => (
                <span
                  key={rec.recipe_id}
                  className="inline-flex items-center gap-1.5 text-[11px] text-[#1A2421] bg-[color:var(--color-primary)]/[0.06] border border-[color:var(--color-primary)]/15 px-2 py-1 rounded"
                >
                  <ChefHat className="w-3 h-3 text-[color:var(--color-primary)]" />
                  <span className="font-medium">{rec.recipe_name}</span>
                  <span className="text-[#5C6B64]">
                    · {rec.planned_qty}
                    <span className="text-[9px] uppercase tracking-wider ml-1">
                      {rec.output_type === 'weight' ? 'kg' : 'portions'}
                    </span>
                  </span>
                </span>
              ))}
            </div>
          )}

          {!plan ? (
            <div className="py-12 text-center">
              <Package className="w-8 h-8 text-[#5C6B64]/30 mx-auto mb-2" />
              <p className="text-sm text-[#5C6B64]">No picklist generated yet</p>
            </div>
          ) : plan.items.length === 0 ? (
            <div className="py-12 text-center">
              <Package className="w-8 h-8 text-[#5C6B64]/30 mx-auto mb-2" />
              <p className="text-sm text-[#5C6B64]">These recipes have no raw ingredients mapped</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold bg-[#F9F9F8] border-b border-[#E2E8DF]">
                    <th className="px-3 py-2 text-left w-10">#</th>
                    <th className="px-3 py-2 text-left">Ingredient</th>
                    <th className="px-3 py-2 text-right">Total Qty</th>
                    <th className="px-3 py-2 text-center">Base Unit</th>
                    <th className="px-3 py-2 text-center">Purchase Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.items.map((it, i) => (
                    <tr key={it.ingredient_id} className="border-b border-[#E2E8DF]/60 hover:bg-[#F9F9F8]"
                      data-testid={`${testIdPrefix}-picklist-row-${i}`}>
                      <td className="px-3 py-2 text-[#5C6B64] tabular-nums">{i + 1}</td>
                      <td className="px-3 py-2 font-medium text-[#1A2421]">{it.ingredient_name}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-[color:var(--color-primary)]">
                        {prettyQty(it.qty_required, it.pack_unit)}
                      </td>
                      <td className="px-3 py-2 text-center text-[11px] uppercase tracking-wider text-[#5C6B64]">{it.pack_unit}</td>
                      <td className="px-3 py-2 text-center text-[11px] uppercase tracking-wider text-[#5C6B64]">{it.purchase_unit || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sticky generate bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-[#E2E8DF] shadow-[0_-4px_16px_rgba(0,0,0,0.04)] z-20">
        <div className="w-full px-3 sm:px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-xs text-[#5C6B64]">
            {validRows.length === 0
              ? 'Add recipes and quantities above to generate a picklist.'
              : `Ready: ${validRows.length} recipe${validRows.length === 1 ? '' : 's'} will be aggregated`}
          </div>
          <Button
            onClick={generate}
            disabled={!canGenerate}
            className="bg-[color:var(--color-primary)] hover:bg-[#0F2A20] text-white h-10 px-6"
            data-testid={`${testIdPrefix}-generate`}
          >
            <Play className="w-4 h-4 mr-2" />
            {generating ? 'Generating…' : 'Generate Aggregated Picklist'}
          </Button>
        </div>
      </div>
    </div>
  );
}
